/**
 * ArtworkSharePage — public, read-only artwork review for external designers.
 *
 * Mounted at /artwork-share/:token. The token is a secure random UUID
 * generated by the customer via the "Share with Designer" dialog. No
 * Owntune account is needed.
 *
 * Capabilities:
 *   • View active + superseded artwork files and corrected repro files
 *   • Read repro partner's status comment
 *   • Download files via short-lived presigned URLs
 *   • Leave comments (Phase 2) that show up inside Owntune for the
 *     customer + admin + repro partner
 *
 * Restrictions: no approve / reject controls, no other order data, no
 * production-side details. The link can be password-protected and/or
 * auto-expire on approval.
 */
import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API_BASE } from '../lib/apiBase';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { useTranslation } from '../contexts/TranslationContext';
import {
    Lock, Download, MessageSquare, Loader2, AlertCircle, Palette, Send, ExternalLink,
} from 'lucide-react';

const API = `${API_BASE}/api`;

// Make sure the public endpoints DON'T receive the customer's auth token.
// `axios.create()` inherits the global `defaults.headers.common.Authorization`
// header set by AuthContext, so we explicitly null it out — otherwise a
// designer opening the link from the same browser as a logged-in customer
// would send the customer's JWT to the public endpoint, which can break
// caching, leak identity, and risk surprising 401s.
const publicAxios = axios.create();
publicAxios.defaults.headers.common = { ...(publicAxios.defaults.headers.common || {}) };
delete publicAxios.defaults.headers.common.Authorization;
publicAxios.interceptors.request.use((config) => {
    if (config.headers) {
        delete config.headers.Authorization;
        delete config.headers.authorization;
    }
    return config;
});

function formatFileSize(bytes) {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
}

/**
 * <FileCommentBlock />
 *
 * Renders every comment attached to a single artwork file so the
 * external designer sees the same context the customer sees on
 * `/orders/:id/artwork-review`. Sources, in priority order:
 *
 *   • `file.comment`            — note attached by the uploader
 *                                 (typically the repro partner on
 *                                 corrected files, or the customer on
 *                                 the original artwork).
 *   • `file.review_comment`     — customer's per-file feedback when
 *                                 requesting changes for that specific
 *                                 file (green if approved, amber
 *                                 otherwise).
 *   • `file.rejection_comment`  — legacy per-file rejection note.
 *   • `file.previous_versions`  — per-version rejection notes on
 *                                 superseded files, shown as a
 *                                 collapsed list underneath.
 *
 * Rendering rule: show each comment on its own row with a small tag
 * indicating who wrote it, so multiple sources on the same file don't
 * blur into a single wall of text. Missing sources render nothing —
 * we don't want empty boxes.
 */
function FileCommentBlock({ file }) {
    const rows = [];
    if (file?.comment) {
        rows.push({
            key: 'comment',
            label: 'Note',
            text: file.comment,
            className: 'bg-slate-50 border-slate-200 text-slate-700',
        });
    }
    if (file?.review_comment) {
        rows.push({
            key: 'review',
            label: 'Customer feedback',
            text: file.review_comment,
            className: 'bg-amber-50 border-amber-200 text-amber-800',
        });
    }
    if (file?.rejection_comment) {
        rows.push({
            key: 'rejection',
            label: 'Rejection',
            text: file.rejection_comment,
            className: 'bg-red-50 border-red-200 text-red-700',
        });
    }
    const previousVersions = (file?.previous_versions || []).filter(
        (pv) => pv?.rejection_comment,
    );

    if (rows.length === 0 && previousVersions.length === 0) return null;

    return (
        <div className="mt-2 space-y-1.5" data-testid={`file-comments-${file.id}`}>
            {rows.map((r) => (
                <div
                    key={r.key}
                    className={`rounded-md border px-2.5 py-1.5 text-xs whitespace-pre-line ${r.className}`}
                    data-testid={`file-comment-${r.key}-${file.id}`}
                >
                    <span className="uppercase tracking-wide text-[10px] font-semibold opacity-70 mr-1.5">
                        {r.label}
                    </span>
                    {r.text}
                </div>
            ))}
            {previousVersions.length > 0 && (
                <div className="pl-2 border-l border-slate-200 space-y-1">
                    {previousVersions.map((pv, idx) => (
                        <div
                            key={pv.id || idx}
                            className="text-[11px] text-slate-500 whitespace-pre-line"
                            data-testid={`file-comment-prev-${file.id}-${idx}`}
                        >
                            <span className="uppercase tracking-wide text-[9px] font-semibold mr-1 opacity-60">
                                Previous rejection
                            </span>
                            {pv.rejection_comment}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function ArtworkSharePage() {
    const { t } = useTranslation();
    const { token } = useParams();

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [requiresPassword, setRequiresPassword] = useState(false);
    const [passwordInput, setPasswordInput] = useState('');
    const [verifiedPassword, setVerifiedPassword] = useState('');

    // Comment form
    const [authorName, setAuthorName] = useState('');
    const [authorEmail, setAuthorEmail] = useState('');
    const [commentText, setCommentText] = useState('');
    const [postingComment, setPostingComment] = useState(false);

    const fetchData = useCallback(async (pw) => {
        setLoading(true);
        setError(null);
        try {
            const headers = pw ? { 'X-Share-Password': pw } : {};
            const res = await publicAxios.get(`${API}/public/artwork-share/${token}`, { headers });
            setData(res.data);
            setRequiresPassword(false);
            if (pw) setVerifiedPassword(pw);
        } catch (e) {
            const status = e.response?.status;
            const detail = e.response?.data?.detail;
            if (status === 401 && (detail?.requires_password || detail === 'Password required')) {
                setRequiresPassword(true);
            } else if (status === 410) {
                setError(typeof detail === 'string' ? detail : 'This link is no longer active.');
            } else if (status === 404) {
                setError('This share link does not exist or has been removed.');
            } else {
                setError(typeof detail === 'string' ? detail : 'Unable to load artwork.');
            }
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Re-hydrate author identity per-browser so the designer doesn't have
    // to retype their name+email for every comment.
    useEffect(() => {
        try {
            setAuthorName(localStorage.getItem('designer_share_name') || '');
            setAuthorEmail(localStorage.getItem('designer_share_email') || '');
        } catch {
            // localStorage blocked — fall back to empty form, no big deal.
        }
    }, []);

    const handleUnlock = async (e) => {
        e?.preventDefault?.();
        if (!passwordInput.trim()) return;
        await fetchData(passwordInput.trim());
    };

    const handleDownload = async (fileId, filename) => {
        try {
            const headers = verifiedPassword ? { 'X-Share-Password': verifiedPassword } : {};
            const res = await publicAxios.get(
                `${API}/public/artwork-share/${token}/download/${fileId}`,
                { headers, responseType: 'json' }
            );
            // S3-backed files return a presigned URL JSON. Local files would
            // return a binary stream — but for our deployment everything is
            // on S3.
            if (res.data?.download_url) {
                window.open(res.data.download_url, '_blank');
            } else {
                toast.error(t('artwork_share.download_link_failed', 'Could not generate download link'));
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || `Failed to download ${filename}`);
        }
    };

    const handlePostComment = async (e) => {
        e?.preventDefault?.();
        if (!authorName.trim() || !commentText.trim()) {
            toast.error(t('artwork_share.name_comment_required', 'Name and comment are required'));
            return;
        }
        setPostingComment(true);
        try {
            const headers = verifiedPassword ? { 'X-Share-Password': verifiedPassword } : {};
            const body = {
                author_name: authorName.trim(),
                author_email: authorEmail.trim() || null,
                message: commentText.trim(),
            };
            const res = await publicAxios.post(
                `${API}/public/artwork-share/${token}/comments`, body, { headers }
            );
            setData((prev) => ({
                ...prev,
                designer_comments: [...(prev?.designer_comments || []), res.data],
            }));
            setCommentText('');
            try {
                localStorage.setItem('designer_share_name', authorName.trim());
                if (authorEmail.trim()) {
                    localStorage.setItem('designer_share_email', authorEmail.trim());
                }
            } catch {
                // localStorage write failed; not critical.
            }
            toast.success(t('artwork_share.comment_posted', 'Comment posted'));
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to post comment');
        } finally {
            setPostingComment(false);
        }
    };

    // ─── Render states ───────────────────────────────────────────────
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
                <Card className="max-w-md w-full">
                    <CardContent className="py-10 text-center">
                        <AlertCircle className="h-10 w-10 mx-auto text-red-400 mb-3" />
                        <h2 className="text-lg font-semibold text-slate-900 mb-1">{t('artwork_share.link_unavailable', 'Link unavailable')}</h2>
                        <p className="text-sm text-slate-500">{error}</p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (requiresPassword) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
                <Card className="max-w-md w-full" data-testid="share-password-prompt">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Lock className="h-5 w-5 text-amber-500" />
                            Password required
                        </CardTitle>
                        <CardDescription>
                            This artwork review link is password-protected. Enter the password from the customer.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleUnlock} className="space-y-3">
                            <Input
                                type="password"
                                value={passwordInput}
                                onChange={(e) => setPasswordInput(e.target.value)}
                                placeholder={t('common.password', 'Password')}
                                autoFocus
                                data-testid="share-password-input"
                            />
                            <Button type="submit" className="w-full" data-testid="share-unlock-btn">
                                Unlock
                            </Button>
                        </form>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (!data) return null;

    const { order, artwork_files, superseded_files, corrected_files, partner_comment, designer_comments } = data;

    return (
        <div className="min-h-screen bg-slate-50">
            <header className="bg-white border-b border-slate-200">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Palette className="h-6 w-6 text-purple-500" />
                        <div>
                            <h1 className="text-lg font-semibold text-slate-900">{t('artwork_share.title', 'Artwork Review')}</h1>
                            <p className="text-xs text-slate-500">
                                Shared with you for review — read-only
                            </p>
                        </div>
                    </div>
                    <Badge className="bg-purple-100 text-purple-700">{t('artwork_share.designer_view', 'Designer view')}</Badge>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
                {/* Order context */}
                <Card>
                    <CardContent className="py-5">
                        <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">{t('artwork_share.order_label', 'Order')}</p>
                        <h2 className="text-xl font-semibold text-slate-900">
                            {order?.title || `Order #${order?.order_number}`}
                        </h2>
                        <div className="flex flex-wrap gap-x-6 gap-y-1 mt-1 text-sm text-slate-500">
                            {order?.artist && <span>{t('artwork_share.artist_prefix', 'Artist:')} <strong className="text-slate-700">{order.artist}</strong></span>}
                            {order?.catalogue_number && <span>{t('artwork_share.catalogue_prefix', 'Catalogue:')} <strong className="text-slate-700">{order.catalogue_number}</strong></span>}
                            {order?.order_number && <span>#{order.order_number}</span>}
                        </div>
                    </CardContent>
                </Card>

                {/* Partner comment */}
                {partner_comment && (
                    <Card data-testid="repro-partner-message">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-base">{t('artwork_share.repro_message', 'Message from repro partner')}</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <p className="text-sm text-slate-700 whitespace-pre-line">{partner_comment}</p>
                        </CardContent>
                    </Card>
                )}

                {/* Corrected files (latest from repro) */}
                {corrected_files?.length > 0 && (
                    <Card>
                        <CardHeader className="pb-3">
                            <CardTitle className="text-base">{t('artwork_share.corrected_from_repro', 'Corrected files from repro')}</CardTitle>
                            <CardDescription>
                                Latest revisions delivered by the repro partner for customer approval.
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <ul className="divide-y divide-slate-100">
                                {corrected_files.map((f) => (
                                    <li key={f.id} className="py-3" data-testid={`corrected-file-${f.id}`}>
                                        <div className="flex items-center justify-between gap-3">
                                            <div className="min-w-0">
                                                <p className="text-sm text-slate-800 truncate">{f.filename}</p>
                                                <p className="text-xs text-slate-400">{formatFileSize(f.size)}</p>
                                            </div>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => handleDownload(f.id, f.filename)}
                                            >
                                                <Download className="h-4 w-4 mr-1" /> Download
                                            </Button>
                                        </div>
                                        <FileCommentBlock file={f} />
                                    </li>
                                ))}
                            </ul>
                        </CardContent>
                    </Card>
                )}

                {/* Original artwork files */}
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base">{t('artwork_share.artwork_files', 'Artwork files')}</CardTitle>
                        <CardDescription>
                            Files the customer originally uploaded for this artwork job.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {artwork_files?.length ? (
                            <ul className="divide-y divide-slate-100">
                                {artwork_files.map((f) => (
                                    <li key={f.id} className="py-3" data-testid={`artwork-file-${f.id}`}>
                                        <div className="flex items-center justify-between gap-3">
                                            <div className="min-w-0">
                                                <p className="text-sm text-slate-800 truncate">{f.filename}</p>
                                                <p className="text-xs text-slate-400">{formatFileSize(f.size)}</p>
                                            </div>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => handleDownload(f.id, f.filename)}
                                            >
                                                <Download className="h-4 w-4 mr-1" /> Download
                                            </Button>
                                        </div>
                                        <FileCommentBlock file={f} />
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="text-sm text-slate-400">{t('artwork_share.no_active_files', 'No active artwork files.')}</p>
                        )}
                    </CardContent>
                </Card>

                {/* Version history */}
                {superseded_files?.length > 0 && (
                    <Card>
                        <CardHeader className="pb-3">
                            <CardTitle className="text-base">{t('artwork_share.version_history', 'Version history')}</CardTitle>
                            <CardDescription>{t('artwork_share.version_history_desc', 'Previous versions, replaced by newer uploads.')}</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <ul className="divide-y divide-slate-100 opacity-80">
                                {superseded_files.map((f) => (
                                    <li key={f.id} className="py-2.5" data-testid={`superseded-file-${f.id}`}>
                                        <div className="flex items-center justify-between gap-3">
                                            <div className="min-w-0">
                                                <p className="text-sm text-slate-600 truncate line-through">{f.filename}</p>
                                                <p className="text-xs text-slate-400">{formatFileSize(f.size)}</p>
                                            </div>
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => handleDownload(f.id, f.filename)}
                                            >
                                                <Download className="h-4 w-4 mr-1" />
                                            </Button>
                                        </div>
                                        <FileCommentBlock file={f} />
                                    </li>
                                ))}
                            </ul>
                        </CardContent>
                    </Card>
                )}

                {/* Designer comments thread */}
                <Card data-testid="designer-comments-thread">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                            <MessageSquare className="h-4 w-4 text-purple-500" />
                            Conversation
                        </CardTitle>
                        <CardDescription>
                            Your comments are visible to the customer and the repro partner inside Owntune.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {designer_comments?.length > 0 ? (
                            <div className="space-y-3">
                                {designer_comments.map((c) => (
                                    <div
                                        key={c.id}
                                        className="border border-slate-200 rounded-lg p-3 bg-white"
                                        data-testid={`comment-${c.id}`}
                                    >
                                        <div className="flex items-baseline justify-between mb-1">
                                            <span className="text-sm font-semibold text-slate-800">{c.author_name}</span>
                                            <span className="text-[11px] text-slate-400">
                                                {new Date(c.created_at).toLocaleString()}
                                            </span>
                                        </div>
                                        <p className="text-sm text-slate-700 whitespace-pre-line">{c.message}</p>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-xs text-slate-400">No comments yet — start the conversation below.</p>
                        )}

                        <form onSubmit={handlePostComment} className="space-y-3 pt-3 border-t border-slate-100">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <Label htmlFor="author-name" className="text-xs text-slate-500">Your name *</Label>
                                    <Input
                                        id="author-name"
                                        value={authorName}
                                        onChange={(e) => setAuthorName(e.target.value)}
                                        placeholder={t('artwork_share.full_name_placeholder', 'Full name')}
                                        className="h-9"
                                        data-testid="designer-name-input"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="author-email" className="text-xs text-slate-500">Email (optional)</Label>
                                    <Input
                                        id="author-email"
                                        type="email"
                                        value={authorEmail}
                                        onChange={(e) => setAuthorEmail(e.target.value)}
                                        placeholder="you@example.com"
                                        className="h-9"
                                        data-testid="designer-email-input"
                                    />
                                </div>
                            </div>
                            <div>
                                <Label htmlFor="comment-text" className="text-xs text-slate-500">Comment *</Label>
                                <Textarea
                                    id="comment-text"
                                    value={commentText}
                                    onChange={(e) => setCommentText(e.target.value)}
                                    placeholder={t('artwork_share.comment_placeholder', 'Please keep black background as 100K only...')}
                                    rows={3}
                                    data-testid="designer-comment-input"
                                />
                            </div>
                            <Button
                                type="submit"
                                disabled={postingComment || !authorName.trim() || !commentText.trim()}
                                className="gap-1.5"
                                data-testid="post-designer-comment-btn"
                            >
                                {postingComment ? (
                                    <><Loader2 className="h-4 w-4 animate-spin" /> Posting...</>
                                ) : (
                                    <><Send className="h-4 w-4" /> Post comment</>
                                )}
                            </Button>
                        </form>
                    </CardContent>
                </Card>

                <p className="text-center text-xs text-slate-400 pt-2">
                    Powered by Owntune
                    <a
                        href="https://flow.owntune.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-1 inline-flex items-center gap-0.5 underline hover:text-slate-600"
                    >
                        <ExternalLink className="h-3 w-3" />
                    </a>
                </p>
            </main>
        </div>
    );
}
